import { makeAutoObservable, runInAction } from "mobx";
import type { User, LoginCredentials, SignUpData, AuthState, AuthTokens } from "../../types";
import { authStorage } from "../../utils/storage.util";
import { apiService } from "../../services/api.service";

export class AuthStore implements AuthState {
  isAuthenticated = false;
  user: User | null = null;
  accessToken: string | null = null;
  refreshToken: string | null = null;

  constructor() {
    makeAutoObservable(this);
    apiService.setAuthFailureHandler(() => {
      this.logout();
    });
    this.loadFromStorage();
  }

  private loadFromStorage(): void {
    try {
      const session = authStorage.getSession();
      if (session) {
        runInAction(() => {
          this.user = session.user;
          this.accessToken = session.tokens.access;
          this.refreshToken = session.tokens.refresh;
          this.isAuthenticated = true;
        });
        apiService.setAuthTokens(session.tokens);
      }
    } catch {
      this.clearSession();
    }
  }

  private saveSession(user: User, tokens: AuthTokens): void {
    authStorage.saveSession({ user, tokens });
    apiService.setAuthTokens(tokens);
  }

  private clearSession(): void {
    authStorage.clearSession();
    apiService.clearSession();
    this.isAuthenticated = false;
    this.user = null;
    this.accessToken = null;
    this.refreshToken = null;
  }

  async login(credentials: LoginCredentials): Promise<void> {
    const response = await apiService.login(credentials);
    
    if (response.success && response.data) {
      const { user, tokens } = response.data;
      runInAction(() => {
        this.user = user;
        this.accessToken = tokens.access;
        this.refreshToken = tokens.refresh;
        this.isAuthenticated = true;
      });
      this.saveSession(user, tokens);
    } else {
      throw new Error(response.error || "Erro ao fazer login");
    }
  }

  async signUp(data: SignUpData): Promise<void> {
    const response = await apiService.signUp(data);
    
    if (response.success && response.data) {
      const { user, tokens } = response.data;
      runInAction(() => {
        this.user = user;
        this.accessToken = tokens.access;
        this.refreshToken = tokens.refresh;
        this.isAuthenticated = true;
      });
      this.saveSession(user, tokens);
    } else {
      throw new Error(response.error || "Erro ao criar conta");
    }
  }

  async refreshSession(): Promise<boolean> {
    if (!this.refreshToken) {
      return false;
    }

    const response = await apiService.refreshAccessToken();
    if (!response.success || !response.data || !this.user) {
      this.clearSession();
      return false;
    }

    const tokens = response.data;
    runInAction(() => {
      this.accessToken = tokens.access;
      this.refreshToken = tokens.refresh;
    });
    this.saveSession(this.user, tokens);

    return true;
  }

  logout(): void {
    this.clearSession();
  }

  // Computed values
  get isLoggedIn(): boolean {
    return (
      this.isAuthenticated &&
      this.user !== null &&
      this.accessToken !== null &&
      this.refreshToken !== null
    );
  }

  get displayName(): string {
    return this.user?.name || "Usuário";
  }

  get userEmail(): string {
    return this.user?.email || "";
  }

  get userType(): string | null {
    return this.user?.userType || null;
  }

  get token(): string | null {
    return this.accessToken;
  }
}